import type { ReactNode, ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

type Variant = 'olive' | 'outline' | 'outline-light' | 'text';
type Size = 'md' | 'lg';

interface BaseProps {
  variant?: Variant;
  size?: Size;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
}

interface ButtonAsLink extends BaseProps {
  to: string;
  href?: never;
  onClick?: never;
  type?: never;
}

interface ButtonAsAnchor extends BaseProps {
  href: string;
  to?: never;
  onClick?: never;
  type?: never;
}

interface ButtonAsButton extends BaseProps, Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled'> {
  to?: never;
  href?: never;
  type?: 'button' | 'submit';
}

type ButtonProps = ButtonAsLink | ButtonAsAnchor | ButtonAsButton;

const sizeClasses: Record<Size, string> = {
  md: 'px-6.5 py-3.5 text-[14.5px]',
  lg: 'px-8 py-4 text-[15px]',
};

const variantClasses: Record<Variant, string> = {
  olive: cn(
    'bg-olive text-paper',
    'hover:bg-gold hover:text-gold-ink hover:-translate-y-0.5',
    'active:bg-gold-press active:text-gold-ink active:translate-y-0 active:scale-[0.97]',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(184,134,60,0.45)]',
  ),
  outline: cn(
    'bg-transparent border border-ink text-ink',
    'hover:bg-olive hover:text-paper hover:border-olive',
    'active:bg-olive-dark active:border-olive-dark',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(33,32,28,0.2)]',
  ),
  'outline-light': cn(
    'bg-paper/10 border border-paper/50 text-paper',
    'hover:bg-paper hover:text-ink hover:border-paper hover:-translate-y-0.5',
    'active:bg-hairline active:translate-y-0 active:scale-[0.97]',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(251,249,245,0.4)]',
  ),
  text: cn(
    'bg-transparent text-olive px-0 py-0 border-b border-transparent rounded-none',
    'hover:text-olive-dark hover:border-olive-dark',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(75,94,40,0.25)] focus-visible:rounded-[3px]',
  ),
};

export default function Button(props: ButtonProps) {
  const { variant = 'olive', size = 'md', pulse, className, children } = props;
  const isText = variant === 'text';

  const classes = cn(
    'inline-flex items-center justify-center font-semibold no-underline transition-all duration-200 ease-out cursor-pointer',
    !isText && 'rounded-full',
    !isText && sizeClasses[size],
    variantClasses[variant],
    pulse && 'hs-pulse',
    className,
  );

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={classes}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    return (
      <a href={props.href} className={classes}>
        {children}
      </a>
    );
  }

  const { onClick, disabled, type } = props as ButtonAsButton;
  return (
    <button type={type ?? 'button'} onClick={onClick} disabled={disabled} className={classes}>
      {children}
    </button>
  );
}
